import { ChevronDown, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { PricedFilter } from './pricedFilters'

interface PricedFilterPopoverProps {
  filter: PricedFilter
  ticked: ReadonlySet<string>
  onToggle: (value: string) => void
  onClear: () => void
}

/** The quotes page's FilterPopover, drawn without its analytics so the prototype logs nothing. */
export function PricedFilterPopover({ filter, ticked, onToggle, onClear }: PricedFilterPopoverProps) {
  const count = ticked.size

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'border-ink-100 gap-1.5 rounded-xl border font-normal',
            count > 0 ? 'border-brand-300 bg-brand-100' : 'bg-transparent'
          )}
        >
          {filter.label}
          <Badge
            variant="secondary"
            aria-hidden={count === 0}
            className={cn(
              'bg-brand-800/15 size-4 min-w-4 px-0 text-[10px] tabular-nums',
              count === 0 && 'invisible'
            )}
          >
            {count}
          </Badge>
          <ChevronDown className="text-ink-300 size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        collisionPadding={8}
        className="w-64 max-w-[calc(100vw-1rem)] p-3"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-bold">{filter.label}</span>
          <PopoverClose asChild>
            <Button type="button" variant="iconOnly" size="icon-sm" aria-label="Close">
              <X className="text-ink-400 size-4" />
            </Button>
          </PopoverClose>
        </div>
        <div className="flex flex-col gap-1">
          {filter.options.map((option) => (
            <label
              key={option.value}
              className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5"
            >
              <Checkbox checked={ticked.has(option.value)} onCheckedChange={() => onToggle(option.value)} />
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
        <div className="mt-2 flex justify-end">
          <Button type="button" variant="ghost" size="sm" disabled={count === 0} onClick={onClear}>
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
